import React from 'react'

import { Box, Typography } from '@mui/material'
import { EMPTY_VALUE, Tooltip } from '@repo/ui'

import { headerWithInfo, INFO_HEADER_ALLOWANCE } from 'components'
import type { SignupFirstTouch } from 'hooks/api/useSignups'
import { date } from 'utils'

/**
 * VRG-728 — "First touch" on People and the classic signups grid: where the visit that submitted
 * came from. Both APIs return the same `first_touch` shape, so one column serves both.
 *
 * One column, not seven. The UTMs, referrer and landing page answer ONE question ("how did they
 * find us"), and seven mostly-empty columns would bury the answer a reviewer actually scans for.
 * The cell shows the most specific thing present; the tooltip shows every field.
 *
 * Precedence, matching the sales pipeline's resolver (`resolveLeadSource`): UTMs, else the
 * referrer host, else the landing page of a direct visit.
 */
export const firstTouchLabel = (t: SignupFirstTouch | null | undefined): string | null => {
  if (!t) return null
  const utm = [t.utm_source, t.utm_medium, t.utm_campaign].filter(Boolean).join(' / ')
  if (utm) return utm
  if (t.referrer_host) return t.referrer_host
  return t.landing_path ? `Direct to ${t.landing_path}` : 'Direct visit'
}

const DETAIL_ROWS: Array<[keyof SignupFirstTouch, string]> = [
  ['utm_source', 'Source'],
  ['utm_medium', 'Medium'],
  ['utm_campaign', 'Campaign'],
  ['utm_content', 'Content'],
  ['utm_term', 'Term'],
  ['referrer_host', 'Referrer'],
  ['landing_path', 'Landing page'],
]

const FirstTouchDetail = ({ touch }: { touch: SignupFirstTouch }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: 'auto auto', columnGap: '12px', rowGap: '2px' }}>
    {DETAIL_ROWS.filter(([k]) => touch[k]).map(([k, label]) => (
      <React.Fragment key={k}>
        <Typography variant="caption" color="inherit" sx={{ opacity: 0.7 }}>
          {label}
        </Typography>
        <Typography variant="caption" color="inherit">
          {touch[k]}
        </Typography>
      </React.Fragment>
    ))}
    <Typography variant="caption" color="inherit" sx={{ opacity: 0.7 }}>
      Recorded
    </Typography>
    <Typography variant="caption" color="inherit">
      {date(touch.at, 'MMM d yyyy, h:mm a')}
    </Typography>
  </Box>
)

const NOTE =
  'Where the visit that submitted came from: its campaign tags, else the site that linked to us, else the page it started on. Recorded for every visitor, cookies or not, but only for that visit, so a visitor who reloaded first shows less. A dash means the submission predates this field.'

const cellSx = { display: 'flex', alignItems: 'center', height: '100%', minWidth: 0 }

/** Any row carrying a `first_touch` (a SignupEntry, a People row). */
type WithFirstTouch = { first_touch?: SignupFirstTouch | null }

export const buildFirstTouchColumn = () => ({
  field: 'first_touch',
  headerName: 'First touch',
  type: 'string',
  // Sized for a typical "linkedin / social / 2026-10-launch"; longer values ellipsize and the
  // tooltip carries the rest. The header ("First touch" + info) needs far less (§49).
  width: 240 + INFO_HEADER_ALLOWANCE,
  sortable: true,
  filterable: false,
  renderHeader: headerWithInfo('First touch', NOTE),
  valueGetter: (_value: unknown, row: WithFirstTouch) => (row ? firstTouchLabel(row.first_touch) : null),
  renderCell: ({ row, value }: { row: WithFirstTouch; value: string | null }) =>
    !value || !row.first_touch ? (
      <Box sx={cellSx}>
        <Typography variant="body2" color="text.disabled">
          {EMPTY_VALUE}
        </Typography>
      </Box>
    ) : (
      <Box sx={cellSx}>
        <Tooltip title={<FirstTouchDetail touch={row.first_touch} />}>
          <Typography variant="body2" noWrap>
            {value}
          </Typography>
        </Tooltip>
      </Box>
    ),
})
